/**
 * Created by rathompson on 10/13/23
 * File: AcquisitionsTable.tsx
 * Project: cloud-mgmt-modules ©
 * ©
 */


import type { VFC } from 'react'
import V5TableWrapper from '../../../common/components/table/V5TableWrapper'
import { ConnectedTable, ConnectedTableFilter, ConnectedTableLayout, TableProvider } from '@panwds/react-table'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  LABEL_CANCEL_ACQ,
  LABEL_VIEW_INFO,
  TABLE_HEADER_ACTIONS,
  TABLE_HEADER_ACTIVITY,
  TABLE_HEADER_REQUEST_DATE,
  TABLE_HEADER_REQUESTED_BY,
  TABLE_HEADER_RESPONSE_DATE,
  TABLE_HEADER_STATUS,
  TABLE_HEADER_TENANT_ID,
  TABLE_HEADER_TENANT_NAME
} from '../../constants/LabelConstants'
import GlobalContext from '../../../common/context/GlobalContext'
import { Body, Bold, Select, useIsMounted, useToast } from '@panwds/react-ui'
import { safeSet } from '../../../common/utils/Utils'
import _ from 'lodash'
import moment from 'moment'
import { useMessage } from '../../../common/providers/MessageProvider'
import styled from 'styled-components'
import { useModal } from '../../../common/components/modal/Modal'
import ViewAcquisitionModal from './ViewAcquisitionModal'
import { CircleCheckIcon, PlusCircleIcon, ProgressCircleIcon } from '@panwds/icons'
import SubtenantAcquisitionModal from '../acquireTenant/SubtenantAcquisitionModal'
import { LoadAcqList } from '../../../common/actions/GlobalActions'
import ActionsRenderer from './cellRenderer/ActionsRenderer'
import type { Row } from 'react-table'
import CancelAcquisitionModal from './CancelAcquisitionModal'

const CONTAINER = styled(V5TableWrapper)`
  min-height: 353px;
  display: flex;
  flex-direction: column;

  [data-testid="connected-table-layout"] {
    & > div {
      flex: 1 1;
    }
  }

  [role="table"] {
    max-height: 300px;
    overflow: auto;
  }
`

const getRequestedBy = (acquisition: any) => {
  if (_.has(acquisition, 'requested_by.first_name') && _.has(acquisition, 'requested_by.last_name')) {
    return `${acquisition.requested_by.first_name} ${acquisition.requested_by.last_name}`
  }
  return acquisition?.requested_by?.email as string
}

const getFormattedDate = (timestamp: any) => (timestamp ? moment(timestamp).format('MM/DD/YYYY') : '')

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'approved':
      return <CircleCheckIcon className="cm-text-success"/>
    case 'rejected':
      return <PlusCircleIcon className="cm-rotate-45 cm-text-error"/>
    case 'pending':
    default:
      return <ProgressCircleIcon/>
  }
}

interface AcquisitionTableRowData {
  activity_id: 'acquisition' | 'acquisition_request',
  activity: 'Acquisition Requested' | 'Acquisition Request Received',
  tsg_id: string,
  requested_by: string,
  request_date: string,
  display_name: string,
  status: string,
  response_date: string,
  acquisition: any
}

const AcquisitionsTable: VFC<any> = () => {

  const isMounted = useIsMounted()
  const { treeReducer, dispatch } = useContext(GlobalContext)
  const { createBanner } = useMessage()
  const { toast } = useToast()
  const { selectedTreeNode, acquisitions, acquisition_requests, acqLoading } = treeReducer

  const viewAcquisitionModal = useModal(ViewAcquisitionModal)
  const subtenantAcquisitionModal = useModal(SubtenantAcquisitionModal)
  const cancelAcquisitionModal = useModal(CancelAcquisitionModal)

  const [data, setData] = useState<AcquisitionTableRowData[]>([])
  const [selectedItem, setSelectedItem] = useState<any>()

  const fetchAcquisitions = useCallback(async (tsg_id) => {
    safeSet(setData, [], isMounted.current)

    await LoadAcqList(tsg_id, dispatch).catch(error => {
      createBanner({ message: error.message })
    })
  }, [createBanner, dispatch, isMounted])

  const onDelete = useCallback((acquisition) => {
    toast(`Acquisition request for TSG "${acquisition.target_tsg_id}" has been deleted`, { appearance: 'success' })
    fetchAcquisitions(selectedTreeNode?.id)
  }, [fetchAcquisitions, selectedTreeNode?.id, toast])

  useEffect(() => {
    const data = []
    if (acquisitions) {
      data.push(...acquisitions.map((acquisition: any) => ({
        activity_id: 'acquisition',
        activity: 'Acquisition Requested',
        tsg_id: acquisition.target_tsg_id,
        requested_by: getRequestedBy(acquisition),
        request_date: getFormattedDate(acquisition.request_timestamp),
        display_name: acquisition.status === 'pending' ? 'pending' : acquisition?.target_tsg?.display_name || 'N/A',
        status: _.capitalize(acquisition.status),
        response_date: getFormattedDate(acquisition.response_timestamp),
        acquisition
      })))
    }
    if (acquisition_requests) {
      data.push(...acquisition_requests.map((acquisition: any) => ({
        activity_id: 'acquisition_request',
        activity: 'Acquisition Request Received',
        tsg_id: acquisition.target_tsg_id,
        requested_by: getRequestedBy(acquisition),
        request_date: getFormattedDate(acquisition.request_timestamp),
        display_name: acquisition?.source_tsg?.display_name || 'N/A',
        status: _.capitalize(acquisition.status),
        response_date: getFormattedDate(acquisition.response_timestamp),
        acquisition
      })))
    }
    safeSet(setData, data, isMounted.current)
  }, [selectedTreeNode.id, acquisitions, acquisition_requests, isMounted])

  const statuses = useMemo(() => {
    const statuses = [
      { value: <Bold>Status: All</Bold>, status: '' }
    ]
    if (data.some((item: any) => item.acquisition.status === 'pending')) {
      statuses.push({ value: 'Pending', status: 'pending' } as any)
    }
    if (data.some((item: any) => item.acquisition.status === 'approved')) {
      statuses.push({ value: 'Approved', status: 'approved' } as any)
    }
    if (data.some((item: any) => item.acquisition.status === 'rejected')) {
      statuses.push({ value: 'Rejected', status: 'rejected' } as any)
    }
    return statuses as any
  }, [data])

  const resolvedData = useMemo(() => {
    data.sort((acq_a: any, acq_b: any) => {
      const time_a = acq_a.acquisition.response_timestamp || acq_a.acquisition.request_timestamp
      const time_b = acq_b.acquisition.response_timestamp || acq_a.acquisition.request_timestamp
      return time_b - time_a
    })
    if (selectedItem) {
      const status = selectedItem.status
      return data.filter((item: any) => item.acquisition.status.startsWith(status))
    }
    return data
  }, [selectedItem, data])

  const onStatusClick = useCallback((row: Row<AcquisitionTableRowData>) => {
    if (row.original.activity_id === 'acquisition' && row.original.acquisition.status === 'pending') {
      viewAcquisitionModal.openModal({ acquisition: row.original.acquisition, onDelete } as any)
    }
    else {
      subtenantAcquisitionModal.openModal({ acquisition: row.original.acquisition } as any)
    }
  }, [onDelete, subtenantAcquisitionModal, viewAcquisitionModal])

  const getActions = useCallback((row) => {
    const actions = []
    actions.push({
      value: LABEL_VIEW_INFO,
      onActionClick: (row: any) => onStatusClick(row)
    })
    if (row.original.activity_id === 'acquisition' && row.original.acquisition.status === 'pending') {
      actions.push({
        value: LABEL_CANCEL_ACQ,
        onActionClick: (row: any) => cancelAcquisitionModal.openModal({ acquisition: row.original.acquisition, onDelete } as any)
      })
    }
    return actions
  }, [cancelAcquisitionModal, onDelete, onStatusClick])

  const columns = useMemo(() => {
    return [
      {
        id: 'activity',
        Header: TABLE_HEADER_ACTIVITY,
        accessor: 'activity',
        width: 220,
        Cell: ({ value }: any) => <Body addClassName="cm-truncate">{value}</Body>
      },
      {
        id: 'tsg_id',
        Header: TABLE_HEADER_TENANT_ID,
        accessor: 'tsg_id',
        Cell: ({ value }: any) => <Body addClassName="cm-truncate">{value}</Body>
      },
      {
        id: 'display_name',
        Header: TABLE_HEADER_TENANT_NAME,
        accessor: 'display_name',
        Cell: ({ value }: any) => <Body addClassName="cm-truncate">{value}</Body>,
        width: 200
      },
      {
        id: 'requested_by',
        Header: TABLE_HEADER_REQUESTED_BY,
        accessor: 'requested_by',
        Cell: ({ value }: any) => <Body addClassName="cm-truncate">{value}</Body>
      },
      {
        id: 'request_date',
        Header: TABLE_HEADER_REQUEST_DATE,
        accessor: 'request_date',
        Cell: ({ value }: any) => <Body addClassName="cm-truncate">{value}</Body>
      },
      {
        id: 'status',
        Header: TABLE_HEADER_STATUS,
        accessor: 'status',
        Cell: ({ row, value }: any) => (
          <div className="cm-flex cm-gap-2">
            {getStatusIcon(row?.original?.acquisition?.status || 'pending')}
            <Body
              addClassName="cm-truncate cm-text-link cm-cursor-pointer"
              onClick={() => onStatusClick(row)}
            >{value}</Body>
          </div>
        )
      },
      {
        id: 'response_date',
        Header: TABLE_HEADER_RESPONSE_DATE,
        accessor: 'response_date',
        Cell: ({ value }: any) => <Body addClassName="cm-truncate">{value}</Body>
      },
      {
        id: 'actions',
        Header: TABLE_HEADER_ACTIONS,
        Cell: ({ row }: any) => <ActionsRenderer actions={getActions(row)} row={row}/>,
        sticky: true,
        stickyPosition: 'right',
        width: 120
      }
    ] as any
  }, [getActions, onStatusClick])

  return (
    <CONTAINER>
      <TableProvider
        isLoading={acqLoading}
        enableColumnOrder
        enablePagination
        enableColumnSort
        enableResizeColumns
        columns={columns}
        data={resolvedData}>
        <div className="cm-flex cm-gap-4 cm-w-full cm-border cm-border-solid cm-border-b-0 cm-p-2 cm-border-gray-300 dark:cm-border-gray-950">
          <ConnectedTableFilter/>
          <Select
            isLoading={acqLoading}
            items={statuses}
            selectedItem={selectedItem ?? statuses[0]}
            onChange={({ selectedItem }) => setSelectedItem(selectedItem)}
          />
        </div>
        <ConnectedTableLayout>
          <ConnectedTable/>
        </ConnectedTableLayout>
      </TableProvider>
    </CONTAINER>
  )
}

export default AcquisitionsTable
